import React from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import InstagramIcon from "@material-ui/icons/Instagram";
import EmailIcon from "@material-ui/icons/Email";
import SmsIcon from "@material-ui/icons/Sms";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
    borderRadius: 8,
    cursor: "pointer",
    height: "100%",
    minHeight: 120,
    position: "relative",
    transition: "all 0.15s ease-in-out",
    "&:hover": {
      borderColor: "#25d366",
      backgroundColor: theme.palette.type === "dark" ? "rgba(37,211,102,0.08)" : "rgba(37,211,102,0.06)",
      transform: "translateY(-2px)",
    },
  },
  cardSelected: {
    borderColor: "#25d366",
    borderWidth: 2,
    backgroundColor: theme.palette.type === "dark" ? "rgba(37,211,102,0.12)" : "rgba(37,211,102,0.08)",
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    marginTop: theme.spacing(1),
    maxWidth: "100%",
    textAlign: "center",
  },
  number: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    maxWidth: "100%",
  },
  typeLabel: {
    fontSize: 10,
    marginTop: 2,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  officialBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    fontSize: 9,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 8,
    backgroundColor: "#128c7e",
    color: "#fff",
  },
}));

const isQrChannel = (type) =>
  !["cloudapi", "instagram", "email", "sms"].includes(String(type || "baileys").toLowerCase());

// Visual por tipo de conexão: WhatsApp Baileys (verde), WhatsApp API Oficial
// (verde-escuro + selo), Instagram, Email e SMS.
// `distinguishOfficial`: o rótulo "Oficial" só faz sentido quando a lista também
// tem conexão via QR Code — sem ela, é só "WhatsApp".
const connectionVisual = (type, distinguishOfficial) => {
  switch (String(type || "baileys").toLowerCase()) {
    case "cloudapi":
      return {
        Icon: WhatsAppIcon,
        color: "#128c7e",
        label: distinguishOfficial ? i18n.t("connectionSelectDialog.whatsappOfficial") : "WhatsApp",
      };
    case "instagram":
      return { Icon: InstagramIcon, color: "#e1306c", label: "Instagram" };
    case "email":
      return { Icon: EmailIcon, color: "#1976d2", label: i18n.t("connectionSelectDialog.email") };
    case "sms":
      return { Icon: SmsIcon, color: "#f5a623", label: "SMS" };
    default:
      return { Icon: WhatsAppIcon, color: "#25d366", label: "WhatsApp" };
  }
};

const isConnected = (status) => String(status || "").toUpperCase() === "CONNECTED";

/**
 * Popup em grade para selecionar a conexão usada em um novo chamado/conversa.
 * Exibe apenas conexões CONECTADAS, com ícone conforme o tipo.
 *
 * Props:
 * - open, onClose
 * - whatsapps: lista de conexões (qualquer status — o filtro é interno)
 * - onSelect(whatsapp | null): chamado ao clicar em uma conexão (null = automático)
 * - selectedWhatsappId: destaca a conexão atualmente escolhida
 * - allowAutomatic: exibe o cartão "Automático (padrão)" que seleciona null
 * - title: título do diálogo
 */
const ConnectionSelectDialog = ({
  open,
  onClose,
  whatsapps,
  onSelect,
  selectedWhatsappId,
  allowAutomatic = false,
  title = i18n.t("connectionSelectDialog.title"),
}) => {
  const classes = useStyles();
  const connectedWhatsapps = (whatsapps || []).filter((w) => isConnected(w.status));
  const hasQrConnection = connectedWhatsapps.some((w) => isQrChannel(w.type));

  const handleSelect = (whatsapp) => {
    if (onSelect) onSelect(whatsapp);
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={() => onClose && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {connectedWhatsapps.length === 0 && !allowAutomatic ? (
          <Typography style={{ padding: 16 }}>
            {i18n.t("connectionSelectDialog.noConnections")}
          </Typography>
        ) : (
          <Grid container spacing={2} style={{ marginBottom: 4 }}>
            {allowAutomatic && (
              <Grid item xs={6} sm={4}>
                <Box
                  className={`${classes.card} ${!selectedWhatsappId ? classes.cardSelected : ""}`}
                  onClick={() => handleSelect(null)}
                >
                  <FlashOnIcon style={{ fontSize: 40, color: "#fbc02d" }} />
                  <Typography noWrap className={classes.name}>
                    {i18n.t("connectionSelectDialog.automatic")}
                  </Typography>
                  <Typography className={classes.typeLabel}>{i18n.t("connectionSelectDialog.systemDefault")}</Typography>
                </Box>
              </Grid>
            )}
            {connectedWhatsapps.map((whatsapp) => {
              const { Icon, color, label } = connectionVisual(whatsapp.type, hasQrConnection);
              const selected = Number(selectedWhatsappId) === Number(whatsapp.id);
              return (
                <Grid item xs={6} sm={4} key={whatsapp.id}>
                  <Box
                    className={`${classes.card} ${selected ? classes.cardSelected : ""}`}
                    onClick={() => handleSelect(whatsapp)}
                  >
                    {hasQrConnection && whatsapp.type === "cloudapi" && (
                      <span className={classes.officialBadge}>{i18n.t("connectionSelectDialog.officialBadge")}</span>
                    )}
                    <Icon style={{ fontSize: 40, color }} />
                    <Typography noWrap className={classes.name}>
                      {whatsapp.name}
                    </Typography>
                    {whatsapp.connectedNumber ? (
                      <Typography noWrap className={classes.number}>
                        {whatsapp.connectedNumber}
                      </Typography>
                    ) : null}
                    <Typography className={classes.typeLabel}>{label}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectionSelectDialog;
